import { isEqual } from 'lodash';
import type { ErrorInfo } from 'react';
import React from 'react';
import { useRecoilValue } from 'recoil';
import {
  type BlockAsOfDateSetting,
  blockAsOfDateSetting,
  type BlockId,
  blockLimitedRequestSubjects,
  type StudioRequestSubject,
} from 'venn-state';
import { HistoricalPortfolioSnapshotNotFoundError, logExceptionIntoSentry } from 'venn-utils';
import StyledEmptyState from '../StyledEmptyState';

type Props = {
  blockId: BlockId;
  children?: React.ReactNode;
};

type State = {
  error: HistoricalPortfolioSnapshotNotFoundError | null;
};

type ErrorBoundaryProps = Props & {
  dependencies: {
    subjects: StudioRequestSubject[];
    blockAsOfDate: BlockAsOfDateSetting;
  };
};

export class HistoricalPortfolioSnapshotNotFoundErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error: error instanceof HistoricalPortfolioSnapshotNotFoundError ? error : null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logExceptionIntoSentry(error, errorInfo);
  }

  componentDidUpdate(prevProps: Readonly<ErrorBoundaryProps>) {
    // Reset error boundary if some dependencies change (e.g. subject is removed)
    if (!isEqual(this.props.dependencies, prevProps.dependencies)) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <StyledEmptyState
          selectedRefId={this.props.blockId}
          header="Unable to run analysis"
          message={
            <>
              Unable to fetch allocations for <span className="font-bold">{this.state.error.portfolio.name}</span>.
              Please ensure your portfolio has sufficient returns and allocations and try again, or contact Venn
              Support.
            </>
          }
        />
      );
    }

    return this.props.children;
  }
}

export const HistoricalPortfolioSnapshotNotFoundErrorWrapper = ({ blockId, children }: Props) => {
  const subjects = useRecoilValue(blockLimitedRequestSubjects(blockId));
  const blockAsOfDate = useRecoilValue(blockAsOfDateSetting(blockId));

  return (
    <HistoricalPortfolioSnapshotNotFoundErrorBoundary blockId={blockId} dependencies={{ subjects, blockAsOfDate }}>
      {children}
    </HistoricalPortfolioSnapshotNotFoundErrorBoundary>
  );
};
